import {
  InputChecklist,
  InputChecklistOption,
  InputTextArea,
} from '@farmshare/ui-components';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import {
  PrimalCut,
  PrimalCutBlocker,
  PrimalCutBlockerBlockedBy,
} from 'lib/graphql';
import { find } from 'lodash';
import { useCallback, useMemo } from 'react';
import { CutsheetFormData } from './types';

export const CutSheetCutGroup: React.FC<{
  cuts: PrimalCut[];
  groupName: string;
  primalCutBlockers: PrimalCutBlocker[];
  action?: (option: InputChecklistOption) => void;
  readOnly?: boolean;
}> = ({ cuts, groupName, primalCutBlockers, readOnly }) => {
  const fP = useFormikContext<CutsheetFormData>();

  const groupedCuts = useMemo(
    () =>
      cuts
        ?.filter((cut) => cut && !cut?.isDeleted)
        .reduce(
          (acc, cut) => {
            if (!cut) return acc;
            const group = cut.primal;
            const name = group?.name ?? 'Uncategorized';
            const existingGroup = acc.find((g) => g.group === name);

            if (existingGroup) {
              existingGroup.cuts.push(cut as PrimalCut);
            } else {
              acc.push({
                group: name,
                id: group?._id ?? 'no-group-id',
                order: group?.order ?? 0,
                cuts: [cut as PrimalCut],
              });
            }
            return acc;
          },
          [] as {
            group: string;
            id: string;
            order: number;
            cuts: PrimalCut[];
          }[],
        )
        .sort((a, b) => a.order - b.order)
        .map((group) => ({
          ...group,
          cuts: group.cuts.sort((a, b) => a.order - b.order),
        })),
    [primalCutBlockers, cuts],
  );

  const blockedCuts = useMemo(
    () =>
      primalCutBlockers
        .filter((blocker) =>
          fP.values.cuts.some((cut) =>
            blocker.blockedBy?.some((b) => b?.primalCutId === cut.value),
          ),
        )
        .reduce(
          (acc, blocker) => {
            acc[blocker.primalCutId] = blocker.blockedBy?.filter((b) =>
              fP.values.cuts.some((cut) => cut.value === b?.primalCutId),
            ) as PrimalCutBlockerBlockedBy[];
            return acc;
          },
          {} as Record<string, PrimalCutBlockerBlockedBy[]>,
        ),
    [primalCutBlockers, fP.values.cuts],
  );

  const getCutOption: (
    cut: PrimalCut,
    includeTag?: boolean,
  ) => InputChecklistOption = useCallback(
    (cut: PrimalCut, includeTag = true) => {
      let disabled = false;
      let description: string | undefined;

      const foundBlockedByItem = blockedCuts[cut._id];

      // This fixes a weird issue where the cut can block itself and you can't untoggle it
      const isSelected = fP.values.cuts.some((c) => c.value === cut._id);

      if (foundBlockedByItem && !isSelected) {
        disabled = true;
        const blockedByNames = foundBlockedByItem
          .map((b) => {
            const blockedCut = find(cuts, (c) => c._id === b.primalCutId);
            return blockedCut?.name ?? 'Unknown Cut';
          })
          .join(', ');
        description = `Blocked By: ${blockedByNames}`;
      }

      return {
        label: cut.name,
        value: cut._id,
        disabled,
        description,
        tag: includeTag && cut.primal?.name,
      };
    },
    [fP.values.blockedBy, blockedCuts],
  );

  return (
    <Card
      elevation={2}
      component={Grid}
      size={{ xs: 12, sm: 6, lg: 4 }}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <CardHeader
        title={groupName}
        // subheader="This is a description for the group"
        slotProps={{
          subheader: {
            sx: { fontSize: '0.8rem', color: 'text.secondary' },
          },
        }}
      />
      <CardContent sx={{ py: 0.5, flex: 1 }}>
        {groupedCuts?.map(({ id, group, cuts: cutsInGroup }) => (
          <div key={id}>
            <InputChecklist
              label={group}
              disabled={readOnly}
              nameOverride="cuts"
              options={cutsInGroup.map((cut) => getCutOption(cut, false))}
            />
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5, fontSize: '0.75rem' }}
              >
                Notes for {group}
              </Typography>
              <InputTextArea
                nameOverride={`notes.${id}`}
                disabled={readOnly}
                label="Notes"
                rows={2}
                showLabel={false}
              />
            </Box>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
